'use client';

import { c as _c } from "react/compiler-runtime";
import { BuildNotifier, BuildNotifierProvider } from 'next-build-notifier';
import type { CSSProperties, ReactNode } from 'react';

/**
 * Trims a full build id to 7 characters — enough to distinguish deployments without cluttering the
 * banner. Falls back to the full string for ids shorter than 7 characters.
 *
 * @param id - The raw build id string (e.g. a full git SHA or Vercel deployment id).
 * @returns The first 7 characters of `id`.
 */
function shortId(id: string): string {
  return id.slice(0, 7);
}

/**
 * Fixed bottom-right banner shown whenever the running admin client is behind the server's current
 * build. Uses Tailwind + CSS custom properties aligned to the admin token contract; no additional
 * runtime dependencies.
 *
 * The entrance slide animates only when the user has not requested reduced motion
 * (`@media (prefers-reduced-motion: no-preference)`). Safe-area insets are applied so the banner
 * avoids home-indicator bars on mobile devices.
 *
 * @param props.currentBuildId - The build id baked into this client at build time.
 * @param props.latestBuildId - The latest build id observed from the server.
 * @param props.onReload - Called when the operator clicks the reload button.
 * @param props.onDismiss - Called when the operator clicks the dismiss button.
 * @returns The positioned banner element.
 */
function AdminBuildNotifierBanner(t0) {
  const $ = _c(21);
  const {
    currentBuildId,
    latestBuildId,
    onReload,
    onDismiss
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      "--nbn-ring-color": "hsl(var(--primary))"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="mt-0.5 flex size-2 shrink-0 rounded-full bg-primary motion-safe:animate-[nbn-ring_600ms_cubic-bezier(0,0,0.2,1)_both]" style={t1 as CSSProperties} aria-hidden="true" />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="font-semibold text-foreground text-sm leading-tight">New build deployed</p>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== currentBuildId) {
    t4 = shortId(currentBuildId);
    $[3] = currentBuildId;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== latestBuildId) {
    t5 = shortId(latestBuildId);
    $[5] = latestBuildId;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <div className="flex items-start gap-3">{t2}<div className="flex min-w-0 flex-col gap-0.5">{t3}<p className="font-mono text-muted-foreground text-xs leading-snug">{t4} → {t5}</p></div></div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== onReload) {
    t7 = <button type="button" onClick={onReload} className="flex-1 rounded-lg bg-primary px-3 py-1.5 font-semibold text-primary-foreground text-xs transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background">Reload</button>;
    $[10] = onReload;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1l12 12M13 1L1 13" /></svg>;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== onDismiss) {
    t9 = <button type="button" onClick={onDismiss} aria-label="Dismiss update notification" className="flex size-7 items-center justify-center rounded-lg border-3 border-border border-solid text-muted-foreground transition-colors hover:border-primary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background">{t8}</button>;
    $[13] = onDismiss;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t7 || $[16] !== t9) {
    t10 = <div className="flex items-center gap-2">{t7}{t9}</div>;
    $[15] = t7;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t6) {
    t11 = <div role="status" aria-live="polite" aria-atomic="true" className="fixed right-4 bottom-4 z-50 [padding-bottom:env(safe-area-inset-bottom,0px)] motion-safe:animate-[nbn-in-bottom_240ms_cubic-bezier(0.22,1,0.36,1)_both]"><div className="flex w-72 flex-col gap-3 rounded-xl border-3 border-border border-solid bg-card/95 p-4 shadow-xl backdrop-blur-sm">{t6}{t10}</div></div>;
    $[18] = t10;
    $[19] = t6;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}

/**
 * Admin build notifier: drives the headless `next-build-notifier` engine and renders the admin-themed
 * banner when a newer build is deployed. Inert in development (`currentBuildId` is `'development'`).
 *
 * Mount once as the last child inside `<Providers>` in the root admin shell layout.
 *
 * @returns The provider + render-prop tree; inert in dev or when no update is available.
 */
export function AdminBuildNotifier() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <BuildNotifierProvider currentBuildId={process.env.NEXT_PUBLIC_BUILD_ID ?? "development"} intervalMs={60000}><BuildNotifier>{_temp}</BuildNotifier></BuildNotifierProvider>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(state) {
  return state.updateAvailable && !state.dismissed && state.latestBuildId ? <AdminBuildNotifierBanner currentBuildId={state.currentBuildId} latestBuildId={state.latestBuildId} onReload={state.reload} onDismiss={state.dismiss} /> : null;
}
AdminBuildNotifier.displayName = 'Nordcom.AdminBuildNotifier';